<template>
  <div
    class="energyEfficiency cursor-pointer"
    :class="{
      'flex items-center': horizontalLayout,
      'flex-row gap-4 justify-center': !reverseOrder && horizontalLayout,
      'flex-row-reverse gap-1 justify-end': reverseOrder && horizontalLayout,
    }"
  >
    <BasicBtErrorBoundary>
      <div :class="{ 'order-2': horizontalLayout }">
        <div
          v-if="energyEfficiencyData?.label?.type === 'LEGACY'"
          class="cheeseAndOnions"
        >
          <div
            v-if="energyEfficiencyData.label.colorPalette"
            class="letterContainer"
            :style="{ color: energyEfficiencyData.label.colorPalette, fontSize: `${labelTextSize}px` }"
            @click.prevent="enableEnergyLabel = true"
          >
            <span
              class="letter"
              :style="{ backgroundColor: energyEfficiencyData.label.colorPalette }"
            >
              {{ energyEfficiencyData.label.letter }}<sup>{{ energyEfficiencyData.label.pluses }}</sup>
            </span>
          </div>
          <div class="letterRange">
            {{ energyEfficiencyData.label.letterRange }}
          </div>
        </div>
        <div
          v-else
          class="saltAndVinegar"
        >
          <NuxtImg
            v-if="energyEfficiencyData.label.letter"
            densities="x1"
            :alt="`Energieeffizienz ${energyEfficiencyData.label.letter} (${energyEfficiencyData.label.letterRange})`"
            :style="{ maxHeight: `${labelSvgSize}px`, minHeight: `${labelSvgSize}px` }"
            :src="energyLabelSrc"
            @click.prevent="enableEnergyLabel = true"
          />
        </div>
      </div>
      <div
        v-if="showDocumentLink"
        class="listDocuments"
        :class="{ 'order-1': horizontalLayout }"
      >
        <div
          class="entry"
          @click.prevent="openInNewTab()"
        >
          Produktdatenblatt
        </div>
      </div>
      <Teleport to="body">
        <div v-if="enableEnergyLabel">
          <BasicModal @close-modal="enableEnergyLabel = false">
            <template #icon>
              <BasicIcon
                :name="'xmark-solid'"
                class="w-4 close-img"
              />
            </template>
            <div
              v-if="labelImage?.originalData"
              class="p-4"
            >
              <img
                :alt="`Energieeffizienz ${energyEfficiencyData.label.letter}`"
                class="mx-auto lg:max-h-[80vh]"
                :src="'https://cdn.expert.de/' + labelImage?.originalData"
              />
            </div>
          </BasicModal>
        </div>
      </Teleport>
    </BasicBtErrorBoundary>
  </div>
</template>

<script setup lang="ts">
import type { Article, MediumData } from '@expert/types';
import { computedAsync } from '@vueuse/core';
import { getMedium } from '~/composables/ExpApi/expCache';

interface Props {
  article: Article;
  showDataFile?: boolean;
  labelSvgSize?: number;
  labelTextSize?: number;
  horizontalLayout?: boolean;
  reverseOrder?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showDataFile: true,
  labelSvgSize: 34,
  labelTextSize: 40,
  horizontalLayout: false,
  reverseOrder: false,
});

const enableEnergyLabel = ref(false);

const energyEfficiencyData: ComputedRef<EnergyEfficiencyData> = computed(() => {
  return getEnergyEfficiencyData(props.article);
});

const labelImage: Ref<MediumData | undefined> = computedAsync(async () => {
  if (energyEfficiencyData.value?.label?.imageId) {
    const { data } = await getMedium(energyEfficiencyData.value.label.imageId);
    return data.value;
  } else {
    return undefined;
  }
});

const showDocumentLink = computed(() => {
  const defaultAndLetter =
    energyEfficiencyData.value?.label?.type === 'DEFAULT' && energyEfficiencyData.value?.label?.letter;
  const legacyAndColorPalette =
    energyEfficiencyData.value?.label?.type === 'LEGACY' && energyEfficiencyData.value?.label?.colorPalette;

  return props.showDataFile && energyEfficiencyData.value?.dataFileId && (defaultAndLetter || legacyAndColorPalette);
});

const energyLabelSrc = computed(() => {
  const lowerCaseLetter = energyEfficiencyData.value?.label?.letter?.toLowerCase() ?? '';
  return `/static/images/icons/efficiencyRating/range/energielabel_${lowerCaseLetter}.svg`;
});

const medium = computedAsync(async () => {
  const id = energyEfficiencyData.value?.dataFileId;

  return id ? (await getMedium(id)) : undefined;
});

const openInNewTab = async () => {
  const result = medium.value;

  if (!result) {
    return;
  }

  const start = Date.now();

  while (result.pending.value) {
    if (Date.now() - start > 1000) {
      console.error('Timed out waiting for medium');
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 50));
  }

  if (!result.data.value?.originalData) {
    return;
  }

  window.open(
    'https://cdn.expert.de/' + result.data.value.originalData,
    '_blank',
    'noopener,noreferrer'
  );
};
</script>
